import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import Stack from "components/common/Stack";
import CKYCauto from "components/Onboarding/CKYCauto";
import CKYCmanual from "components/Onboarding/CKYCmanual";

import { validationHelper } from "helper/validation";
import { cameraHelper } from "helper/camera";
import JarvisButton from "components/JarvisButton";
import { dateTimeHelper } from "helper/dateTime";
import { masterApis } from "stores/master/masterApis";
// import { config } from "config";
import { localStore } from "stores/localStorage";

import { useSelector, useDispatch } from "react-redux";
import { commonActions } from "stores/common/commonActions";
import { PHCOnboardingActions } from "stores/PHC/PHCOnboarding/PHCOnboardingAction";
import { config } from "config/index";
import { resolveCkycFormData } from "helper/ckycPrefill";
import {
  buildKycUploadUpdate,
  buildMergedKycUploadDisplayState,
  emptyCkycDocStatus,
  getKycCrossProductProps,
  kycDocumentUploadSucceeded,
  mergeCkycDocStatusWithUploadFlags,
  ckycStatusFlagsFromProps,
} from "helper/kycDocumentUpload";
import {
  applyCkycUploadFailure,
  applyCkycUploadResult,
  areKycDocumentsReadyForSubmit,
  getCkycCustomerName,
  getKycUploadCatchMessage,
  isManualKycVerified,
  loadCkycCustomerName,
  requestCkycDocumentVerify,
  validateKycDocumentUpload,
} from "helper/ckycDocumentVerify";

function PHCCkyc() {
  const history = useHistory();
  const dispatch = useDispatch();

  // ------------------------ Redux State ------------------------
  const {
    currentStage,
    panNumber,
    dateOfBirth,
    aadharNumber,
    isPanUploaded,
    isAadhaarFrontUploaded,
    isAadhaarBackUploaded,
    panVerification,
    aadharFrontVerification,
    aadharBackVerification,
    isManualKYC,
  } = useSelector((state) => state.PHC.PHCOnboarding);

  const loginProfile = useSelector((state) => state.customer.loginProfile);
  const { crossProductKycUploadFlags, disableUpload } =
    useSelector((state) => getKycCrossProductProps(state, config.productCodes.PHC));

  // ------------------------ Local State ------------------------
  const [formData, setFormData] = useState({
    panNumber: "",
    aadharNumber: "",
    dateOfBirth: "",
    inputIdType: "C",
  });

  const [manageForm, setManageForm] = useState({
    docType: "",
    docVerify: false,
  });

  const [formError, setFormError] = useState({
    aadharNumber: "",
    panNumber: "",
    dateOfBirth: "",
    panCard: "",
    aadharCardFront: "",
    aadharCardBack: "",
  });

  const [imagesUploadingLoader, setImagesUploadingLoader] = useState({
    panCard: false,
    aadharCardFront: false,
    aadharCardBack: false,
  });

  const [localUploads, setLocalUploads] = useState({
    panCard: false,
    aadharCardFront: false,
    aadharCardBack: false,
  });
  const [documentVerifyStatus, setDocumentVerifyStatus] = useState(() =>
    mergeCkycDocStatusWithUploadFlags(
      emptyCkycDocStatus(),
      ckycStatusFlagsFromProps({
        isPanUploaded,
        isAadhaarFrontUploaded,
        isAadhaarBackUploaded,
        panVerification,
        aadharFrontVerification,
        aadharBackVerification,
      }),
    ),
  );

  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [enableManually, setEnableManually] = useState(true);
  const [hasFirstAttempError, setHasFirstAttempError] = useState(false);
  const ckycUploadAppliedRef = useRef(false);

  // ------------------------ ComponentDidMount ------------------------
  useEffect(() => {
    if (
      localStore.isSsoCustomer() &&
      localStore.getSsoCustomerDetails()?.brokerName ===
        config.broker.ssoPlatform.centrum
    ) {
      history.replace({
        pathname: "/customer/document-signing",
        state: {
          returnURI: "/phc/onboarding",
          callOrigin: "PHC",
          productCode: config.productCodes.PHC,
        },
      });
    }

    if (currentStage === "CKYC") {
      setFormData((prev) => ({
        ...prev,
        panNumber: panNumber || prev.panNumber,
        dateOfBirth: dateOfBirth || prev.dateOfBirth,
        aadharNumber: aadharNumber || prev.aadharNumber,
      }));
    }

    if (currentStage === "DOCUMENT_SIGNING") {
      setFormData({
        panNumber,
        dateOfBirth,
      });
      setIsVerified(true);
    }

    if (
      currentStage === "CKYC" &&
      (isPanUploaded || isAadhaarFrontUploaded || isAadhaarBackUploaded)
    ) {
      setEnableManually(true);
    }
  }, []);

  // Preview-link users (Navia banner / OneMoney) have no loginProfile name
  useEffect(() => {
    loadCkycCustomerName(dispatch, getCkycCustomerName(loginProfile));
  }, [dispatch]);

  // Prefill PAN / Aadhaar / DOB when details API updates Redux
  useEffect(() => {
    if (currentStage !== "CKYC" && currentStage !== "DOCUMENT_SIGNING") {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      ...resolveCkycFormData(
        loginProfile,
        { panNumber, dateOfBirth, aadharNumber },
        prev,
      ),
    }));
  }, [currentStage, panNumber, dateOfBirth, aadharNumber, loginProfile]);

  useEffect(() => {
    if (ckycUploadAppliedRef.current) {
      return;
    }
    setDocumentVerifyStatus((prev) =>
      mergeCkycDocStatusWithUploadFlags(
        prev,
        ckycStatusFlagsFromProps({
          isPanUploaded,
          isAadhaarFrontUploaded,
          isAadhaarBackUploaded,
          panVerification,
          aadharFrontVerification,
          aadharBackVerification,
        }),
      ),
    );
  }, [
    isPanUploaded,
    isAadhaarFrontUploaded,
    isAadhaarBackUploaded,
    panVerification,
    aadharFrontVerification,
    aadharBackVerification,
  ]);

  // Re-derive isVerified whenever the real onboarding stage arrives/changes,
  // instead of relying only on the mount-time snapshot above.
  useEffect(() => {
    setIsVerified(currentStage === "DOCUMENT_SIGNING");
  }, [currentStage]);

  // ------------------------ Handlers ------------------------
  const handleChange = ({ name, value }) => {
    if (name === "docType") {
      setManageForm((prev) => ({ ...prev, docType: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setFormError((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleValidation = () => {
    const panValidation = validationHelper.pan(formData.panNumber);
    const dobValidation = validationHelper.dateOfBirth(formData.dateOfBirth);

    setFormError((prev) => ({
      ...prev,
      panNumber: panValidation.message,
      dateOfBirth: dobValidation.message,
    }));

    return panValidation.isValid && dobValidation.isValid;
  };

  // ------------------------ Auto Verification ------------------------
  const handleVerification = async () => {
    if (!handleValidation()) return;

    setIsLoading(true);
    setHasFirstAttempError(false);

    try {
      const res = await masterApis.verfyAutoKYC({
        dateOfBirth: dateTimeHelper.dateOfBirth(formData.dateOfBirth),
        pan: formData.panNumber,
      });

      if (res.code === "KYC009") {
        await masterApis.submitKYCApi({ productCode: "PHC" });

        setIsVerified(true);
        dispatch(PHCOnboardingActions.getOnboardingData());
      } else {
        setHasFirstAttempError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ------------------------ Re-Verification Validation ------------------------
  const handleValidationReVerification = () => {
    const pan = validationHelper.pan(formData.panNumber);
    const dob = validationHelper.dateOfBirth(formData.dateOfBirth);
    const aadhar = validationHelper.aadhar(formData.aadharNumber);
    const mergedImagesUploaded = buildMergedKycUploadDisplayState(
      {
        isPanUploaded,
        isAadhaarFrontUploaded,
        isAadhaarBackUploaded,
      },
      crossProductKycUploadFlags,
      localUploads,
    );
    const docsReady = areKycDocumentsReadyForSubmit(
      mergedImagesUploaded,
      disableUpload,
      documentVerifyStatus,
    );

    setFormError({
      ...formError,
      panNumber: pan.message,
      dateOfBirth: dob.message,
      aadharNumber: aadhar.message,
      panCard: !mergedImagesUploaded.panCard ? "Verify PAN photo" : "",
      aadharCardFront: !mergedImagesUploaded.aadharCardFront
        ? "Verify Aadhaar Front"
        : "",
      aadharCardBack: !mergedImagesUploaded.aadharCardBack
        ? "Upload Aadhaar Back"
        : "",
    });

    return pan.isValid && dob.isValid && aadhar.isValid && docsReady;
  };

  // ------------------------ Re Verification ------------------------
  const handleReVerification = async () => {
    if (!handleValidationReVerification()) return;

    setIsLoading(true);
    setHasFirstAttempError(false);

    try {
      const res = await masterApis.verifyManualKyc({
        panNumber: formData.panNumber,
        aadhaarNumber: formData.aadharNumber,
        dateOfBirth: formData.dateOfBirth,
        productCode: config.productCodes.PHC,
      });

      if (isManualKycVerified(res)) {
        setIsVerified(true);
        setEnableManually(false);

        dispatch(
          PHCOnboardingActions.updatePHCOnboardingData({
            isManualKYC: res.result?.isVerified,
          }),
        );

        dispatch(PHCOnboardingActions.getOnboardingData());
      } else {
        dispatch(commonActions.setToast({ message: res.message }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ------------------------ File Upload Handler ------------------------
  const onBrowseClick = async ({ type, data = null, source } = {}) => {
    const customerName = getCkycCustomerName(loginProfile);
    const fieldValidation = validateKycDocumentUpload({
      type,
      formData,
      customerName,
    });
    if (!fieldValidation.isValid) {
      setFormError((prev) => ({ ...prev, ...fieldValidation.errors }));
      if (fieldValidation.errors.customerName) {
        dispatch(
          commonActions.setToast({
            message: fieldValidation.errors.customerName,
          }),
        );
      }
      return;
    }

    let fileData = data;
    if (fileData?.name) {
      const fileExtension = fileData.name.split(".").pop().toLowerCase();
      fileData.format = fileExtension;
    }

    const isNativeCamera =
      config.platform !== "WEB" && source === "camera";
    if (isNativeCamera) {
      fileData = await cameraHelper.getKycNativeDocumentPhoto();
    }

    if (!fileData) return;

    let file;
    if (isNativeCamera) {
      file = await cameraHelper.getBlobFromCameraPhoto(fileData);
      if (!file || file.size === 0) {
        dispatch(
          commonActions.setToast({
            message: "Could not read the photo. Please try again.",
          }),
        );
        return;
      }
    } else {
      file = await readFileAsBlob(fileData);
      if (!file || file.size === 0) {
        dispatch(
          commonActions.setToast({
            message: "Could not read the selected file. Please try again.",
          }),
        );
        return;
      }
    }

    const kb = file.size / 1024;
    if (kb > 10000) {
      dispatch(
        commonActions.setToast({
          message: `Image must be less than 10MB`,
        }),
      );
      return;
    }

    setImagesUploadingLoader((prev) => ({ ...prev, [type]: true }));

    try {
      const res = await requestCkycDocumentVerify({
        type,
        file,
        fileName: `${type}_${loginProfile.customerId}_${Date.now()}`,
        originalFileName: fileData?.name,
        productCode: config.productCodes.PHC,
        loginProfile,
        formData,
      });

      const succeeded = kycDocumentUploadSucceeded(res, type);
      ckycUploadAppliedRef.current = true;
      const applied = applyCkycUploadResult(
        { formError, documentVerifyStatus },
        type,
        res,
      );
      setFormError(applied.formError);
      setDocumentVerifyStatus(applied.documentVerifyStatus);
      dispatch(
        PHCOnboardingActions.updatePHCOnboardingData(
          buildKycUploadUpdate(
            {
              isPanUploaded,
              isAadhaarFrontUploaded,
              isAadhaarBackUploaded,
            },
            type,
            res,
          ),
        ),
      );
      setLocalUploads((prev) => ({ ...prev, [type]: succeeded }));
    } catch (e) {
      const applied = applyCkycUploadFailure(
        { formError, documentVerifyStatus },
        type,
        getKycUploadCatchMessage(e),
        e,
      );
      setLocalUploads((prev) => ({ ...prev, [type]: false }));
      setFormError(applied.formError);
      setDocumentVerifyStatus(applied.documentVerifyStatus);
    } finally {
      setImagesUploadingLoader((prev) => ({ ...prev, [type]: false }));
    }
  };

  const readFileAsBlob = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) =>
        resolve(new Blob([e.target.result], { type: file.type }));
      reader.readAsArrayBuffer(file);
    });

  // ------------------------ Proceed to Document Signing ------------------------
  const handleProceedDocumentSigningClick = () => {
    let pathname = "/customer/document-signing";
    if (loginProfile.isManualAgreement === 1) {
      pathname = "/manual-document-signing";
    }
    history.push({
      pathname,
      state: {
        returnURI: "/phc/onboarding",
        callOrigin: "PHC",
        productCode: config.productCodes.PHC,
      },
    });
  };

  // ------------------------ Render ------------------------
  const isImagesUploaded = buildMergedKycUploadDisplayState(
    {
      isPanUploaded,
      isAadhaarFrontUploaded,
      isAadhaarBackUploaded,
    },
    crossProductKycUploadFlags,
    localUploads,
  );
  const showProceedCta = isVerified;

  return (
    <>
      <Stack sx={{ flex: 1 }}>
        {enableManually ? (
          <CKYCmanual
            formData={formData}
            formError={formError}
            isVerified={isVerified}
            isLoading={isLoading}
            isImagesUploaded={isImagesUploaded}
            imagesUploadingLoader={imagesUploadingLoader}
            handleChange={handleChange}
            onBrowseClick={onBrowseClick}
            handleReVerification={handleReVerification}
            toggleKYCtype={setEnableManually}
            disableUpload={disableUpload}
            documentVerifyStatus={documentVerifyStatus}
          />
        ) : (
          <CKYCauto
            formData={formData}
            formError={formError}
            isVerified={isVerified}
            isLoading={isLoading}
            hasFirstAttempError={hasFirstAttempError}
            handleChange={handleChange}
            handleVerification={handleVerification}
            toggleKYCtype={setEnableManually}
            initialAadharNumber={formData.aadharNumber}
          />
        )}

        {showProceedCta && (
          <Stack direction="row" justifyContent="center" mt={6} mb={3}>
            <JarvisButton
              loading={isLoading}
              onClick={handleProceedDocumentSigningClick}
            >
              Proceed to Client Agreement
            </JarvisButton>
          </Stack>
        )}
      </Stack>
    </>
  );
}

export default PHCCkyc;
